import { useEffect, useState } from 'react'
import type { QueryKey } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { ApiError } from '@/lib/apiClient'
import { cn } from '@/lib/cn'
import { fromZonedInput, toZonedInput } from '@/lib/datetime'

import { EducationSuggestions } from '@/features/competition/components'
import {
  ACTIVITY_MAX,
  EDUCATION_MAX,
  MOTTO_MAX,
  WORD_MAX,
  toWordSlots,
  validateProfile,
  wordsPayload,
  type ProfileField,
} from '@/features/competition/profile'

import { useAdminMutation } from '../api'
import { AdminHeader } from '../AdminLayout'
import { AwardsPanel } from '../components/AwardsPanel'
import { CollectionEditor, FormActions } from '../components/CollectionEditor'
import {
  I18nField,
  NumberField,
  SelectField,
  SwitchField,
  TextField,
  type I18nValue,
} from '../components/fields'
import { JuryPanel } from '../components/JuryPanel'
import { MediaField } from '../components/MediaField'
import { useAdminContext } from '../session'
import { useCollection } from '../useCollection'
import type {
  Candidate,
  CompetitionCategory,
  CompetitionRound,
  I18n,
  ProgramItem,
} from '../types'

/**
 * Miss & Master : catégories et candidatures.
 *
 * Deux principes structurent l'écran :
 *
 * * **Une candidature ne se modifie pas librement.** Elle contient des
 *   données personnelles qu'une personne a saisies elle-même. On l'approuve
 *   ou on la refuse — avec un motif —, on lui attribue un dossard. Réécrire
 *   son nom ou son e-mail à sa place n'aurait aucun sens, et le serveur ne
 *   le permet d'ailleurs pas. Seuls la photo et le profil public (âge,
 *   activité, études, adage, trois mots) se complètent ici — pour les
 *   candidatures déposées avant que le formulaire ne les demande.
 * * **Le dossard est attribué par le serveur.** Laisser le champ vide fait
 *   prendre le prochain numéro libre de la catégorie — deux personnes ne
 *   peuvent pas se retrouver avec le même, même approuvées en même temps.
 */
export function AdminCompetitionPage() {
  const { t, i18n } = useTranslation()
  const { eventId, event, can } = useAdminContext()
  const timezone = event?.timezone ?? 'Europe/Berlin'

  useEffect(() => {
    document.title = t('admin.section.competition')
  }, [t])

  const categories = useCollection<CompetitionCategory>({
    key: 'admin-competition-categories',
    path: `/admin/events/${eventId}/competition/categories`,
    eventId,
    related: [['competition-categories'], ['admin-readiness', eventId ?? '']],
  })
  const candidates = useCollection<Candidate>({
    key: 'admin-candidates',
    path: `/admin/events/${eventId}/competition/candidates`,
    eventId,
    related: [['competition-candidates'], ['admin-readiness', eventId ?? '']],
  })
  // Les tours du défilé : tenue culturelle, tenue de ville, tenue de soirée.
  // L'API existait depuis le début ; aucun écran ne les créait, si bien qu'on
  // ne pouvait rattacher un scrutin à rien et que le cumul n'avait pas de
  // colonnes. On en crée ici, on vote dessus dans « Votes ».
  const rounds = useCollection<CompetitionRound>({
    key: 'admin-competition-rounds',
    path: `/admin/events/${eventId}/competition/rounds`,
    eventId,
  })
  // Les étapes du déroulé : un tour qui y est rattaché démarre quand le MC
  // lance l'étape au conducteur, et se termine avec elle.
  const programItems = useCollection<ProgramItem>({
    key: 'admin-program-items',
    path: `/admin/events/${eventId}/program-items`,
    eventId,
  })

  function label(value: I18n): string {
    return value[i18n.language] || value[Object.keys(value)[0] ?? ''] || '—'
  }

  const categoryName = (id: string) =>
    label(categories.items.find((category) => category.id === id)?.name_i18n ?? {})

  return (
    <>
      <AdminHeader
        title={t('admin.section.competition')}
        description={t('admin.competition.description')}
      />

      {/* `grid-cols-1` (minmax(0, 1fr)) : sans colonne déclarée, l'adresse
          d'un juré, tronquée mais insécable, imposait sa largeur à toute la
          page sur téléphone. */}
      <div className="grid grid-cols-1 gap-6">
        <CollectionEditor<CompetitionCategory>
          title={t('admin.competition.categories')}
          description={t('admin.competition.categoriesHint')}
          items={categories.items}
          busy={categories.busy}
          error={categories.error}
          emptyLabel={t('admin.competition.noCategories')}
          // Chaque bouton suit son droit : sans lui, le clic n'aboutissait
          // qu'à un refus du serveur.
          createLabel={
            can('competition.category.create') ? t('admin.competition.addCategory') : null
          }
          onDelete={(category) => categories.remove.mutate({ id: category.id })}
          canDelete={() => can('competition.category.delete')}
          summarize={(category) => {
            const count = candidates.items.filter(
              (candidate) => candidate.competition_category_id === category.id,
            ).length
            return {
              title: label(category.name_i18n),
              subtitle: category.code,
              value:
                category.max_participants === null
                  ? t('admin.competition.applicants', { count })
                  : `${count} / ${category.max_participants}`,
              dimmed: !category.is_active,
            }
          }}
          renderForm={(category, close) => (
            <CategoryForm
              category={category}
              timezone={timezone}
              canSave={
                category ? can('competition.category.update') : can('competition.category.create')
              }
              busy={categories.busy}
              onCancel={close}
              onSubmit={(payload) => {
                const done = { onSuccess: () => close() }
                if (category) categories.update.mutate({ id: category.id, ...payload }, done)
                else categories.create.mutate(payload, done)
              }}
            />
          )}
        />

        {/* Le jury de chaque titre : trois ou quatre personnes qui notent
            chaque tour de 1 à 10, 30 % de la note. Composer le jury,
            c'est configurer le concours — même droit. */}
        {can('competition.category.update') && categories.items.length > 0 ? (
          <section className="space-y-4">
            <div>
              <h2 className="font-display text-heading-lg text-content-primary">
                {t('admin.jury.title')}
              </h2>
              <p className="mt-1 max-w-2xl text-sm text-content-secondary">
                {t('admin.jury.intro')}
              </p>
            </div>
            {categories.items.map((category) => (
              <JuryPanel
                key={category.id}
                eventId={eventId}
                competitionCategoryId={category.id}
                title={label(category.name_i18n)}
              />
            ))}
          </section>
        ) : null}

        <CollectionEditor<CompetitionRound>
          title={t('admin.competition.rounds')}
          description={t('admin.competition.roundsHint')}
          items={rounds.items}
          busy={rounds.busy}
          error={rounds.error}
          emptyLabel={t('admin.competition.noRounds')}
          createLabel={can('competition.round.create') ? t('admin.competition.addRound') : null}
          onDelete={(round) => rounds.remove.mutate({ id: round.id })}
          canDelete={(round) => can('competition.round.delete') && round.status === 'pending'}
          summarize={(round) => ({
            title: label(round.name_i18n),
            subtitle: round.competition_category_id
              ? categoryName(round.competition_category_id)
              : t('admin.competition.roundAllCategories'),
            badges: [
              {
                label: t(`admin.competition.roundStatus.${round.status}`),
                tone:
                  round.status === 'running'
                    ? ('success' as const)
                    : round.status === 'done'
                      ? ('neutral' as const)
                      : ('warning' as const),
              },
            ],
          })}
          renderForm={(round, close) => (
            <RoundForm
              round={round}
              categories={categories.items}
              programItems={programItems.items}
              categoryLabel={label}
              canSave={round ? can('competition.round.update') : can('competition.round.create')}
              busy={rounds.busy}
              onCancel={close}
              onSubmit={(payload) => {
                const done = { onSuccess: () => close() }
                if (round) rounds.update.mutate({ id: round.id, ...payload }, done)
                else rounds.create.mutate(payload, done)
              }}
            />
          )}
        />

        <CollectionEditor<Candidate>
          title={t('admin.competition.candidates')}
          description={t('admin.competition.candidatesHint')}
          items={candidates.items}
          busy={candidates.busy}
          error={candidates.error}
          emptyLabel={t('admin.competition.noCandidates')}
          // Une candidature ne se crée pas ici : elle est déposée par la
          // personne elle-même, avec ses consentements.
          createLabel={null}
          onDelete={(candidate) => candidates.remove.mutate({ id: candidate.id })}
          canDelete={() => can('competition.candidate.delete')}
          summarize={(candidate) => ({
            title: [candidate.stage_name, `${candidate.first_name} ${candidate.last_name}`]
              .filter(Boolean)
              .join(' — '),
            subtitle: categoryName(candidate.competition_category_id),
            value: candidate.candidate_number ? `N° ${candidate.candidate_number}` : '',
            dimmed: candidate.status === 'rejected' || candidate.status === 'withdrawn',
            badges: [
              {
                label: t(`admin.competition.status.${candidate.status}`),
                tone:
                  candidate.status === 'approved'
                    ? ('success' as const)
                    : candidate.status === 'pending'
                      ? ('warning' as const)
                      : ('neutral' as const),
              },
            ],
          })}
          renderForm={(candidate, close) =>
            candidate ? (
              <CandidateReview
                candidate={candidate}
                eventId={eventId}
                can={can}
                onDone={close}
                onCancel={close}
              />
            ) : null
          }
        />

        {/* Les prix et leur podium (« Prix du public », « Best Mask »…) :
            l'API existait, aucun écran ne s'en servait. */}
        <AwardsPanel
          eventId={eventId}
          competitions={categories.items}
          candidates={candidates.items}
          label={label}
          can={can}
        />
      </div>
    </>
  )
}

/**
 * Un tour du défilé.
 *
 * Le rattachement à un concours n'est pas décoratif : c'est lui qui fait que
 * les voix de « tenue de soirée » s'additionnent à celles de « tenue de
 * ville » pour désigner Miss. Un tour rattaché à rien vit seul, et son score
 * ne compte dans aucun titre.
 */
function RoundForm({
  round,
  categories,
  programItems,
  categoryLabel,
  canSave,
  busy,
  onSubmit,
  onCancel,
}: {
  round: CompetitionRound | null
  categories: CompetitionCategory[]
  programItems: ProgramItem[]
  categoryLabel: (value: I18n) => string
  canSave: boolean
  busy: boolean
  onSubmit: (payload: Record<string, unknown>) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState<I18nValue>(round?.name_i18n ?? {})
  const [description, setDescription] = useState<I18nValue>(round?.description_i18n ?? {})
  const [category, setCategory] = useState(round?.competition_category_id ?? '')
  const [programItem, setProgramItem] = useState(round?.program_item_id ?? '')

  return (
    <div className="grid gap-5">
      <I18nField label={t('admin.competition.roundName')} value={name} onChange={setName} />
      <I18nField
        label={t('admin.competition.roundDescription')}
        value={description}
        onChange={setDescription}
      />
      <SelectField
        label={t('admin.competition.roundCategory')}
        hint={t('admin.competition.roundCategoryHint')}
        value={category}
        onChange={setCategory}
        options={[
          { value: '', label: t('admin.competition.roundAllCategories') },
          ...categories.map((entry) => ({
            value: entry.id,
            label: categoryLabel(entry.name_i18n),
          })),
        ]}
      />
      <SelectField
        label={t('admin.competition.roundProgramItem')}
        hint={t('admin.competition.roundProgramItemHint')}
        value={programItem}
        onChange={setProgramItem}
        options={[
          { value: '', label: t('admin.competition.roundNoProgramItem') },
          ...programItems.map((entry) => ({
            value: entry.id,
            label: categoryLabel(entry.title_i18n),
          })),
        ]}
      />
      {canSave ? (
        <FormActions
          busy={busy}
          onCancel={onCancel}
          onSubmit={() =>
            onSubmit({
              name_i18n: name,
              description_i18n: description,
              competition_category_id: category || null,
              program_item_id: programItem || null,
            })
          }
        />
      ) : null}
    </div>
  )
}

function CategoryForm({
  category,
  timezone,
  canSave,
  busy,
  onSubmit,
  onCancel,
}: {
  category: CompetitionCategory | null
  /** Le fuseau de l'édition : les dates se saisissent à l'heure de la salle. */
  timezone: string
  canSave: boolean
  busy: boolean
  onSubmit: (payload: Record<string, unknown>) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [code, setCode] = useState(category?.code ?? '')
  const [name, setName] = useState<I18nValue>(category?.name_i18n ?? {})
  const [description, setDescription] = useState<I18nValue>(category?.description_i18n ?? {})
  const [rules, setRules] = useState<I18nValue>(category?.rules_i18n ?? {})
  const [maxParticipants, setMaxParticipants] = useState<number | null>(
    category?.max_participants ?? null,
  )
  const [active, setActive] = useState(category?.is_active ?? true)
  // La fenêtre des candidatures : le formulaire public s'ouvre et se ferme
  // seul. Elle n'était modifiable que par l'API.
  const [opensAt, setOpensAt] = useState(
    toZonedInput(category?.applications_open_at ?? null, timezone),
  )
  const [closesAt, setClosesAt] = useState(
    toZonedInput(category?.applications_close_at ?? null, timezone),
  )

  const codeIsFixed = category !== null

  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2">
        {codeIsFixed ? null : (
          <TextField
            label={t('admin.tickets.code')}
            hint={t('admin.competition.codeHint')}
            value={code}
            onChange={(value) => setCode(value.toUpperCase())}
          />
        )}
        <I18nField label={t('admin.food.name')} value={name} onChange={setName} />
        <I18nField
          label={t('admin.food.descriptionField')}
          value={description}
          onChange={setDescription}
        />
        <I18nField
          label={t('admin.competition.rules')}
          multiline
          value={rules}
          onChange={setRules}
        />
        <NumberField
          label={t('admin.competition.maxParticipants')}
          hint={t('admin.competition.maxParticipantsHint')}
          value={maxParticipants}
          min={1}
          max={1000}
          onChange={setMaxParticipants}
        />
        <SwitchField
          label={t('admin.active')}
          hint={t('admin.activeHint')}
          value={active}
          onChange={setActive}
        />
        <TextField
          label={t('admin.competition.applicationsOpenAt')}
          hint={t('admin.competition.applicationsWindowHint', { timezone })}
          type="datetime-local"
          value={opensAt}
          onChange={setOpensAt}
        />
        <TextField
          label={t('admin.competition.applicationsCloseAt')}
          hint={t('admin.competition.applicationsWindowHint', { timezone })}
          type="datetime-local"
          value={closesAt}
          onChange={setClosesAt}
        />
      </div>
      {canSave ? (
        <FormActions
          busy={busy || (!codeIsFixed && !/^[A-Z0-9_]{2,}$/.test(code))}
          onCancel={onCancel}
          onSubmit={() =>
            onSubmit({
              ...(codeIsFixed ? {} : { code }),
              name_i18n: name,
              description_i18n: description,
              rules_i18n: rules,
              max_participants: maxParticipants,
              is_active: active,
              applications_open_at: fromZonedInput(opensAt, timezone),
              applications_close_at: fromZonedInput(closesAt, timezone),
            })
          }
        />
      ) : null}
    </div>
  )
}

/**
 * L'examen d'une candidature.
 *
 * On approuve ou on refuse ; on ne réécrit pas ce que la personne a saisi.
 * Le motif de refus n'est pas décoratif : il part dans le message qu'elle
 * recevra, et un refus sans explication est le meilleur moyen de recevoir dix
 * messages en retour.
 */
function CandidateReview({
  candidate,
  eventId,
  can,
  onDone,
  onCancel,
}: {
  candidate: Candidate
  eventId: string | undefined
  can: (permission: string) => boolean
  onDone: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [reason, setReason] = useState('')
  const [number, setNumber] = useState<number | null>(candidate.candidate_number)
  const [photoId, setPhotoId] = useState<string | null>(candidate.photo_media_id)

  const invalidate = [
    ['admin-candidates', eventId],
    ['competition-candidates'],
    ['admin-readiness', eventId ?? ''],
  ]

  // La photo s'enregistre seule, sans passer par l'approbation : on ajoute
  // la photo d'un candidat déjà approuvé sans le réapprouver.
  const savePhoto = useAdminMutation<Candidate, { photo_media_id: string | null }>(
    'patch',
    () => `/admin/events/${eventId}/competition/candidates/${candidate.id}`,
    eventId,
    invalidate,
  )

  const approve = useAdminMutation<Candidate, { candidate_number: number | null }>(
    'post',
    () => `/admin/events/${eventId}/competition/candidates/${candidate.id}/approve`,
    eventId,
    invalidate,
  )
  const reject = useAdminMutation<Candidate, { reason: string }>(
    'post',
    () => `/admin/events/${eventId}/competition/candidates/${candidate.id}/reject`,
    eventId,
    invalidate,
  )

  const busy = approve.isPending || reject.isPending || savePhoto.isPending
  const canReview = can('competition.candidate.review')
  const canUpdate = can('competition.candidate.update')
  const error = approve.error ?? reject.error ?? savePhoto.error

  return (
    <div>
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        <Row label={t('admin.competition.fullName')} value={`${candidate.first_name} ${candidate.last_name}`} />
        <Row label={t('admin.competition.stageName')} value={candidate.stage_name ?? '—'} />
        <Row label={t('admin.competition.email')} value={candidate.email} />
        <Row label={t('admin.competition.phone')} value={candidate.phone ?? '—'} />
        <Row label={t('admin.competition.age')} value={candidate.age ? String(candidate.age) : '—'} />
        <Row
          label={t('admin.competition.statusLabel')}
          value={t(`admin.competition.status.${candidate.status}`)}
        />
      </dl>

      {/* Le profil public : modifiable avec `candidate.update`, lisible sinon. */}
      {canUpdate ? (
        <ProfileEditor candidate={candidate} eventId={eventId} invalidate={invalidate} />
      ) : (
        <dl className="mt-4 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <Row label={t('candidateProfile.activity')} value={candidate.activity ?? '—'} />
          <Row
            label={t('candidateProfile.educationLevel')}
            value={candidate.education_level ?? '—'}
          />
          <Row label={t('candidateProfile.motto')} value={candidate.motto ?? '—'} />
          <Row
            label={t('candidateProfile.threeWords')}
            value={candidate.three_words.length ? candidate.three_words.join(' · ') : '—'}
          />
        </dl>
      )}

      {candidate.rejection_reason ? (
        <p className="mt-4 rounded-md border border-line-subtle px-3 py-2 text-xs text-content-muted">
          {t('admin.competition.rejectionReason')} : {candidate.rejection_reason}
        </p>
      ) : null}

      {error instanceof ApiError ? (
        <p className="mt-4 text-sm text-state-danger">{error.message}</p>
      ) : null}

      {/* Chaque geste suit son droit : la photo (`candidate.update`),
          l'examen (`candidate.review`). Sans eux, on lit la fiche. */}
      {canUpdate ? (
        <div className="mt-5">
          <MediaField
            label={t('admin.media.candidatePhoto')}
            hint={t('admin.media.candidatePhotoHint')}
            value={photoId}
            eventId={eventId}
            onChange={(value) => {
              setPhotoId(value)
              savePhoto.mutate({ photo_media_id: value })
            }}
          />
        </div>
      ) : null}

      {canReview ? (
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <NumberField
            label={t('admin.competition.number')}
            hint={t('admin.competition.numberHint')}
            value={number}
            min={1}
            max={999}
            onChange={setNumber}
          />
          <TextField
            label={t('admin.competition.reason')}
            hint={t('admin.competition.reasonHint')}
            value={reason}
            onChange={setReason}
          />
        </div>
      ) : null}

      <div className="mt-5 flex flex-wrap gap-2">
        {canReview ? (
          <>
            <Button
              size="sm"
              // Approuver n'a de sens que pour une candidature en attente ou refusée :
              // sur un finaliste ou un vainqueur, cela le renverrait en simple approuvé.
              disabled={busy || !['pending', 'rejected'].includes(candidate.status)}
              onClick={() => approve.mutate({ candidate_number: number }, { onSuccess: onDone })}
            >
              {t('admin.competition.approve')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy || !reason.trim()}
              onClick={() => reject.mutate({ reason: reason.trim() }, { onSuccess: onDone })}
            >
              {t('admin.competition.reject')}
            </Button>
          </>
        ) : null}
        <Button variant="ghost" size="sm" disabled={busy} onClick={onCancel}>
          {t('admin.cancel')}
        </Button>
      </div>
    </div>
  )
}

/**
 * Le profil public d'une candidature : âge, activité, études, adage, trois mots.
 *
 * Facultatif ici, contrairement au formulaire public : une candidature
 * déposée avant qu'il ne soit demandé se complète à la main, au fil des
 * échanges avec la personne. Un champ vidé disparaît du profil public ;
 * les trois mots restent trois, ou aucun — le serveur le vérifie aussi.
 * Il s'enregistre seul, sans repasser par l'approbation.
 */
function ProfileEditor({
  candidate,
  eventId,
  invalidate,
}: {
  candidate: Candidate
  eventId: string | undefined
  invalidate: QueryKey[]
}) {
  const { t } = useTranslation()
  const [age, setAge] = useState(candidate.age ? String(candidate.age) : '')
  const [activity, setActivity] = useState(candidate.activity ?? '')
  const [educationLevel, setEducationLevel] = useState(candidate.education_level ?? '')
  const [motto, setMotto] = useState(candidate.motto ?? '')
  const [words, setWords] = useState(toWordSlots(candidate.three_words))

  const save = useAdminMutation<Candidate, Record<string, unknown>>(
    'patch',
    () => `/admin/events/${eventId}/competition/candidates/${candidate.id}`,
    eventId,
    invalidate,
  )

  const errors = validateProfile(
    { age, activity, educationLevel, motto, words },
    { required: false },
  )
  const errorText = (field: ProfileField) => {
    const error = errors[field]
    return error ? t(error.key, error.params ?? {}) : undefined
  }
  const invalid = Object.keys(errors).length > 0

  return (
    <section className="mt-5 rounded-lg border border-line-subtle p-4">
      <h3 className="font-display text-heading-sm text-content-primary">
        {t('candidateProfile.adminTitle')}
      </h3>
      <p className="mt-1 text-xs text-content-muted">{t('candidateProfile.adminHint')}</p>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <TextField
          label={t('admin.competition.age')}
          hint={errorText('age') ?? ''}
          value={age}
          onChange={(value) => setAge(value.replace(/\D/g, ''))}
        />
        <TextField
          label={t('candidateProfile.activity')}
          hint={errorText('activity') ?? ''}
          value={activity}
          maxLength={ACTIVITY_MAX}
          onChange={setActivity}
        />
        <TextField
          label={t('candidateProfile.educationLevel')}
          hint={errorText('education_level') ?? t('candidateProfile.educationHint')}
          value={educationLevel}
          maxLength={EDUCATION_MAX}
          list={`education-suggestions-${candidate.id}`}
          onChange={setEducationLevel}
        />
        <TextField
          label={t('candidateProfile.motto')}
          hint={errorText('motto') ?? ''}
          value={motto}
          maxLength={MOTTO_MAX}
          onChange={setMotto}
        />
      </div>
      <EducationSuggestions id={`education-suggestions-${candidate.id}`} />

      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        {words.map((word, index) => (
          <TextField
            key={index}
            label={t('candidateProfile.word', { n: index + 1 })}
            value={word}
            maxLength={WORD_MAX}
            onChange={(value) => {
              const next: [string, string, string] = [...words]
              next[index] = value
              setWords(next)
            }}
          />
        ))}
      </div>
      <p
        className={cn(
          'mt-1.5 text-xs',
          errors.three_words ? 'text-state-danger' : 'text-content-muted',
        )}
      >
        {errorText('three_words') ?? t('candidateProfile.adminWordsHint')}
      </p>

      {save.error instanceof ApiError ? (
        <p className="mt-3 text-sm text-state-danger">{save.error.message}</p>
      ) : null}
      {save.isSuccess && !save.isPending ? (
        <p className="mt-3 text-sm text-state-success">{t('candidateProfile.adminSaved')}</p>
      ) : null}

      <div className="mt-4">
        <Button
          size="sm"
          variant="outline"
          disabled={save.isPending || invalid}
          onClick={() =>
            save.mutate({
              age: age ? Number(age) : null,
              // Vide = effacé : le serveur enregistre « rien », pas une chaîne vide.
              activity: activity.trim() || null,
              education_level: educationLevel.trim() || null,
              motto: motto.trim() || null,
              three_words: wordsPayload(words),
            })
          }
        >
          {t('candidateProfile.adminSave')}
        </Button>
      </div>
    </section>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className={cn('flex justify-between gap-4 border-b border-line-subtle py-1.5')}>
      <dt className="text-content-muted">{label}</dt>
      <dd className="text-right text-content-primary">{value}</dd>
    </div>
  )
}
